// LEGENDA ISU, pe ecran — ce înseamnă culorile stratului «Rezistență la foc».
//
// Tot tabelul claselor, nu doar cele de pe nivel (Oana, 20.10.2026): cine
// citește planșa trebuie să vadă unde stă portocaliul lui EI 120 între
// celelalte, nu o legendă care se schimbă de la un nivel la altul. Eșantionul e
// cel al planșei de referință — dreptunghi plin în culoarea clasei, cu hașura
// neagră peste —, iar emblemele ușilor sunt aceleași cercuri care se desenează
// pe goluri (`StoreyView.EmblemaGol`), ca legenda să explice exact semnul de
// pe desen. Culorile și textele vin din `rezistentaFoc.ts`: un singur dicționar.

import { Fragment } from "react";
import { useT } from "../../i18n";
import { CLASE, CULOARE_CLASA, GRI_ALA, MARO_FOAIE } from "./rezistentaFoc";

/** Dreptunghiul hașurat al legendei de referință: umplerea e clasa, liniile
 *  sunt negre, ca pe planșă. Desenate ca `<line>`, nu ca `<pattern>` — v. nota
 *  din `hasura.ts` despre de ce. */
function Esantion({ culoare }: { culoare: string }) {
  const W = 44, H = 14, pas = 6;
  const linii: number[] = [];
  for (let x = -H; x < W; x += pas) linii.push(x);
  return (
    <svg width={W} height={H} aria-hidden="true" style={{ display: "block" }}>
      <rect x={0.5} y={0.5} width={W - 1} height={H - 1} fill={culoare} stroke="#111" />
      {linii.map((x) => (
        // ce iese din dreptunghi taie chiar `<svg>`-ul: fără `clipPath` cu id,
        // care pe trei legende deodată s-ar fi ciocnit (v. `planseu-clip-id`)
        <line key={x} x1={x} y1={H} x2={x + H} y2={0} stroke="#111" strokeWidth={0.8} />
      ))}
    </svg>
  );
}

/** Emblema ușii, cât un rând de legendă. */
function Cerc({ culoare, randuri }: { culoare: string; randuri: readonly string[] }) {
  const D = 26, doua = randuri.length > 1;
  return (
    <svg width={44} height={D} viewBox={`0 0 44 ${D}`} aria-hidden="true" style={{ display: "block" }}>
      <circle cx={22} cy={D / 2} r={D / 2 - 1} fill="#fff" stroke={culoare} strokeWidth={1.3} />
      {randuri.map((r, k) => (
        <text key={k} x={22} y={D / 2 + (doua ? (k === 0 ? -4.5 : 5) : 0)}
              fontSize={doua ? 7.5 : 9} fontWeight={700} fill={culoare}
              fontFamily="system-ui, sans-serif" textAnchor="middle"
              dominantBaseline="central">{r}</text>
      ))}
    </svg>
  );
}

/** O coloană a legendei: eșantion + text, rând cu rând. */
const coloana = { display: "grid", gridTemplateColumns: "auto 1fr", gap: "4px 8px",
                  alignItems: "center", alignContent: "start" } as const;

export default function LegendaFoc() {
  const t = useT();
  // DOUĂ COLOANE (Oana, 23.10.2026): pereții în stânga, ușile în dreapta. Într-o
  // singură coloană cardul ieșea mai înalt decât planșa de sub el și o acoperea.
  return (
    <div className="legenda-isu" data-testid="legenda-isu"
         style={{ display: "grid", gridTemplateColumns: "auto auto", gap: "6px 18px",
                  alignItems: "start", fontSize: 12, lineHeight: 1.25 }}>
      <div style={{ gridColumn: "1 / -1", fontWeight: 600 }}>{t("legenda.isu.titlu")}</div>
      <div style={coloana} data-testid="legenda-isu-pereti">
        {CLASE.map((c) => (
          <Fragment key={c}>
            <Esantion culoare={CULOARE_CLASA[c]} />
            <span style={{ color: CULOARE_CLASA[c], fontWeight: 600, whiteSpace: "nowrap" }}>
              R {c} / REI {c} / EI {c}
            </span>
          </Fragment>
        ))}
        <Esantion culoare={GRI_ALA} />
        <span style={{ maxWidth: 190 }}>{t("legenda.isu.ala")}</span>
      </div>
      {/* textele ușilor se rup pe rânduri: fără plafon, cel mai lung ar lăți cardul la loc */}
      <div style={{ ...coloana, maxWidth: 290 }} data-testid="legenda-isu-usi">
        <Cerc culoare={CULOARE_CLASA[60]} randuri={["EI₂", "60-C"]} />
        <span>{t("legenda.isu.usa_ei")}</span>
        <Cerc culoare={MARO_FOAIE} randuri={["UM"]} />
        <span>{t("legenda.isu.um")}</span>
        <Cerc culoare={MARO_FOAIE} randuri={["OM"]} />
        <span>{t("legenda.isu.om")}</span>
      </div>
    </div>
  );
}
